import { ChevronUp } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

import { cn } from '@/lib/utils';

// The designer's accordion primitives, ported from SessionDetailContent.css:
//   .sc-accordion-card / -header / -title / -body        → <Accordion>
//   .sc-sub-accordion-card / -header / -title / -body    → <SubAccordion>
//   .sc-sub-card / .sc-sub-card-title                    → <SubCard>
//   .sc-kv-row / .sc-kv-label / .sc-kv-value             → <KeyValueRow>
//
// Values are taken from that stylesheet rather than eyeballed: card border #CBD5E1,
// header padding clamp(12px,0.9vw,16px), body padding clamp(12px,1.1vw,18px) with
// padding-top 0, sub-card border #B3B3B3, label column min-width clamp(120px,10vw,180px).
//
// The header is a real <button> rather than a div with onClick (the design uses a div),
// so the accordion is reachable by keyboard and announces its expanded state. The
// design's chevron behaviour is kept exactly: one ChevronUp that rotates 180° when
// collapsed, so the icon animates instead of swapping.

const HEADER_PAD = 'p-[clamp(12px,0.9vw,16px)]';
const BODY_PAD = 'p-[clamp(12px,1.1vw,18px)] pt-0';
const BODY_GAP = 'gap-[clamp(12px,1.1vw,18px)]';

export function Accordion({
  icon,
  title,
  right,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
}: {
  /**
   * Rendered as-is, not a component type — the step accordions pass the workflow
   * `<StepIcon>` (the same masked SVG the sidebar and the sessions list use), which is a
   * node rather than a lucide component.
   */
  icon: ReactNode;
  title: string;
  /** Rendered at the far right of the header, before the chevron (e.g. a status pill). */
  right?: ReactNode;
  /** Initial state when the caller does NOT control `open`. */
  defaultOpen?: boolean;
  /**
   * Controlled mode. Pass `open` (with `onOpenChange`) when something outside the header
   * must be able to expand this card — the step chips do exactly that, and a card that
   * only ever owned its own state could be scrolled to but not opened.
   *
   * Omit both and the accordion manages itself from `defaultOpen`.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const bodyId = useId();

  // In controlled mode the parent owns the state, so we only report the intent. Writing
  // local state as well would leave two sources of truth that disagree the moment the
  // parent declines the change.
  const toggle = () => {
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className="shrink-0 overflow-hidden rounded-lg border border-[#CBD5E1] bg-white shadow-2xs transition-all duration-200">
      {/* The ENTIRE header row is the toggle, as in the design — clicking the title, the
          status pill, the empty space between them or the chevron all collapse the card.
          A single <button> spanning the row does that and stays keyboard-reachable, which
          the design's clickable <div> would not be.

          This is only safe while `right` is non-interactive (a status pill). If a control
          ever needs to live there, it has to move out of this button rather than nest
          inside it — nested buttons are invalid HTML and one click would fire both. */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className={cn(
          'flex w-full items-center justify-between gap-2.5 bg-white text-left transition-colors hover:bg-[#F9FAFB]',
          HEADER_PAD,
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="flex shrink-0 items-center text-[#303030]">{icon}</span>
          <span className="truncate text-[length:var(--text-18)] font-medium text-[#041A3A]">
            {title}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-2.5">
          {right}
          <ChevronUp
            size={18}
            className={cn(
              'text-[#4B5563] transition-transform duration-200',
              !open && 'rotate-180',
            )}
          />
        </span>
      </button>

      {open && (
        <div id={bodyId} className={cn('flex flex-col bg-white', BODY_PAD, BODY_GAP)}>
          {children}
        </div>
      )}
    </div>
  );
}

/** The design's inner accordion (`Risk Rules`) — smaller type, lighter border. */
export function SubAccordion({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    // rounded-lg with a VISIBLE border (#D9DEE7 rather than the design's #ECEEF3, which is
    // so close to white that the corners read as a straight rule across the card instead
    // of a rounded box). `overflow-hidden` clips the header's hover fill to the radius —
    // without it the fill paints square corners over the rounded border.
    <div className="mt-2 overflow-hidden rounded-lg border border-[#D9DEE7] bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center justify-between gap-2.5 bg-white p-[clamp(10px,0.8vw,12px)] text-left transition-colors hover:bg-[#F9FAFB]"
      >
        <h4 className="m-0 text-[length:clamp(13px,0.9vw,15px)] font-medium text-[#041A3A]">
          {title}
        </h4>
        <ChevronUp
          size={16}
          className={cn('text-[#4B5563] transition-transform duration-200', !open && 'rotate-180')}
        />
      </button>

      {open && (
        <div id={bodyId} className="bg-white p-[clamp(10px,0.8vw,12px)] pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

/** `.sc-sub-card` — the bordered white box the accordion bodies are built from. */
export function SubCard({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-[#B3B3B3] bg-white p-[clamp(12px,1vw,16px)]',
        className,
      )}
    >
      <h4 className="m-0 text-[length:var(--text-16)] font-semibold text-[#041A3A]">{title}</h4>
      {children}
    </div>
  );
}

/** `.sc-details-kv-grid` — the label/value stack inside a SubCard. */
export function KeyValueList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>;
}

/**
 * `.sc-kv-row` — label, then the value immediately after it.
 *
 * The value is NOT pushed to the card's right edge. The design's `.sc-kv-value` carries
 * `text-right`, but its row is a plain flex with a min-width label and a content-width
 * value, so that `text-right` never has any room to act on — the values render hard
 * against the label column, which is what their screenshots show. Pushing them to the
 * far edge (an `ml-auto` here) stranded every value metres from its label on a wide card.
 *
 * The label column keeps its min-width so the values still start on one shared line
 * instead of stepping in and out with each label's length.
 *
 * `value` is a node, not a string, because the design puts status glyphs in this column.
 */
export function KeyValueRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    // STACKS ON A PHONE, side-by-side from `sm` up.
    //
    // The label column is `min-w-[clamp(120px,10vw,180px)]`, which is the aligned look the design
    // wants on a wide card — but on a 320px screen it eats 120px before the value starts, leaving a
    // verified email or a filename about 190px to wrap inside. Stacking gives the value the full
    // width, which is how a phone reads a label/value pair anyway.
    <div className="flex flex-col gap-0.5 text-[length:var(--text-14)] sm:flex-row sm:items-start sm:gap-3">
      <span className="font-normal text-[#666666] sm:min-w-[clamp(120px,10vw,180px)] sm:shrink-0">
        {label}
      </span>
      {/* `break-words` matters more once stacked: a long unbroken token (a storage key, an email
          with no spaces) would otherwise widen the card rather than wrap inside it. */}
      <span className="flex min-w-0 items-center gap-1.5 break-words font-medium text-[#303030]">
        {value}
      </span>
    </div>
  );
}

/** The two-column grid the design uses for Overview and for each step's body. */
export function TwoColumnGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-[clamp(12px,1.1vw,16px)] md:grid-cols-2">{children}</div>
  );
}
